/**
 * WhatsApp templates + conversation starters slice of the English catalog.
 *
 * Spread into `enIntegrations` (not en.ts, which sits at the 500-line
 * ceiling), so t() and MessageKey cover these keys unchanged.
 * Customer-facing copy — changes go through Marketing.
 */
export const enMessagingAssets = {
  // ── Template status / category labels (canvas picker + Settings) ─────
  'waTemplates.status.APPROVED': 'Approved',
  'waTemplates.status.PENDING': 'Pending review',
  'waTemplates.status.REJECTED': 'Rejected',
  'waTemplates.status.PAUSED': 'Paused',
  'waTemplates.status.DISABLED': 'Disabled',
  'waTemplates.category.MARKETING': 'Marketing',
  'waTemplates.category.UTILITY': 'Utility',
  'waTemplates.category.AUTHENTICATION': 'Authentication',

  // ── Canvas: WhatsApp template node picker ─────────────────────────────
  'waTemplates.picker.label': 'Approved template',
  'waTemplates.picker.placeholder': 'Choose an approved template…',
  'waTemplates.picker.loading': 'Loading templates…',
  'waTemplates.picker.empty': 'No approved templates yet — create or sync them under Account → Integrations → WhatsApp.',
  'waTemplates.picker.loadFailed': "Couldn't load your templates. Check the WhatsApp connection and try again.",
  'waTemplates.picker.notFound': 'Not in your synced templates — sync them in Account → Integrations, or choose another.',
  'waTemplates.picker.option': '{name} · {language} · {category}',

  // ── Settings: WhatsApp templates section ──────────────────────────────
  'waTemplates.section.title': 'WhatsApp templates',
  'waTemplates.section.subtitle': 'Templates Meta has approved let you message customers after the 24-hour reply window closes.',
  'waTemplates.sync': 'Sync from WhatsApp',
  'waTemplates.syncDone': 'Templates synced — {count} found.',
  'waTemplates.syncFailed': "Couldn't sync the templates.",
  'waTemplates.loadFailed': "Couldn't load the templates.",
  'waTemplates.retry': 'Try again',
  'waTemplates.empty': 'No templates yet. Sync to load the ones already in your WhatsApp account, or create a new one.',
  'waTemplates.rejectedReason': 'Reason from Meta: {reason}',
  'waTemplates.delete': 'Delete',
  'waTemplates.deleteConfirm': 'Delete “{name}” ({language}) from your WhatsApp account? This cannot be undone.',
  'waTemplates.deleted': 'Template deleted.',
  'waTemplates.deleteFailed': "Couldn't delete the template.",
  'waTemplates.new': 'New template',

  // ── Settings: create-template form ────────────────────────────────────
  'waTemplates.form.name': 'Name',
  'waTemplates.form.nameHint': 'Lowercase letters, numbers and underscores — e.g. order_update',
  'waTemplates.form.language': 'Language code',
  'waTemplates.form.languageHint': 'For example en, en_US or he',
  'waTemplates.form.category': 'Category',
  'waTemplates.form.header': 'Header (optional)',
  'waTemplates.form.body': 'Message',
  'waTemplates.form.bodyHint': 'Use {{1}}, {{2}} … for the parts that change for each customer.',
  'waTemplates.form.example': 'Example value for variable {index}',
  'waTemplates.form.footer': 'Footer (optional)',
  'waTemplates.form.buttons': 'Buttons (optional)',
  'waTemplates.form.buttonsHint': 'Up to 3 quick replies, or up to 2 link or phone buttons.',
  'waTemplates.form.addQuickReply': 'Add quick reply',
  'waTemplates.form.addUrl': 'Add link button',
  'waTemplates.form.addPhone': 'Add phone button',
  'waTemplates.form.buttonText': 'Button text',
  'waTemplates.form.removeButton': 'Remove button',
  'waTemplates.form.submit': 'Submit for review',
  'waTemplates.form.cancel': 'Cancel',
  'waTemplates.form.submitted': 'Template submitted to Meta for review.',
  'waTemplates.form.failed': "Couldn't submit the template.",

  // ── Settings: conversation starters ───────────────────────────────────
  'starters.title': 'Conversation starters',
  'starters.subtitle':
    'Tap-to-start questions and a persistent menu — each opens the chat flow you choose.',
  'starters.channel.messenger': 'Messenger',
  'starters.channel.instagram': 'Instagram',
  'starters.channel.whatsapp': 'WhatsApp',
  'starters.iceBreakers': 'Ice breakers',
  'starters.iceBreakersHint': 'Up to 4 questions shown when a customer opens a new chat.',
  'starters.question': 'Question',
  'starters.flow': 'Flow',
  'starters.chooseFlow': 'Choose a flow…',
  'starters.flowInactive': '{name} (not active)',
  'starters.addIceBreaker': 'Add question',
  'starters.menu': 'Persistent menu',
  'starters.menuHint': 'Up to 3 items that stay in the chat menu.',
  'starters.menuTitle': 'Title',
  'starters.menuTypeFlow': 'Start a flow',
  'starters.menuTypeLink': 'Open a link',
  'starters.addMenuItem': 'Add menu item',
  'starters.commands': 'Commands',
  'starters.commandsHint': 'Shortcuts customers can type with “/”, e.g. /hours.',
  'starters.command': 'Command',
  'starters.commandDescription': 'Description',
  'starters.addCommand': 'Add command',
  'starters.remove': 'Remove',
  'starters.save': 'Save and publish',
  'starters.result.ok': '{channel}: published.',
  'starters.result.failed': '{channel}: not published — {reason}',
  'starters.result.notConnected': '{channel}: not connected — connect it first, then save again.',
  'starters.saveFailed': "Couldn't save the conversation starters.",
  'starters.loadFailed': "Couldn't load the conversation starters.",
  'starters.noFlows': 'Create a chat flow first — each starter opens one.',
  'starters.lastPushed': 'Last published {date}',
} as const;
